/**
 * ローカルストレージ保存（Phaser 非依存 / storage 差し替え可能）。
 *
 * 保存内容:
 *  - ハイスコア
 *  - 音量・表示などの設定
 *  - 初めて作成した料理の図鑑状態
 *  - プレイ回数
 *  - 最高到達レベル
 *
 * localStorage が使えない環境（プライベートモード等）や、
 * 保存データが壊れている場合でもゲームは起動する。
 */

import { DEFAULT_SETTINGS, type GameSettings } from '../config/gameConfig';
import { migrateIdList, type StageIdMigration } from '../config/idMigrations';

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface SaveData {
  version: number;
  highScore: number;
  /**
   * いまのハイスコアが、コンティニュー・おはしを使ったプレイの記録か。
   * 結果画面で「（おはし・コンティニューあり）」と添えるのに使う（ランキングには入らないため）。
   * 項目の無い古いデータは false（どちらだったか分からないので印を付けない）。
   */
  highScoreAssisted: boolean;
  /**
   * コンティニューもおはしも使わなかったプレイだけのハイスコア。
   * 項目の無い古いデータは highScore と同じ値にする。
   */
  highScoreNoAssist: number;
  /** 全プレイの合計スコア。「食べに行く」ごほうびの解放条件に使う。 */
  totalScore: number;
  playCount: number;
  maxLevelReached: number;
  /**
   * コンティニューもアイテムも使わずに到達した最高レベル（「ノーコン」勲章の判定用）。
   * この項目が無い古いデータは、アシストが存在しなかった時代の記録なので maxLevelReached と同じ値にする。
   */
  bestLevelNoAssist: number;
  unlockedItemIds: string[];
  /**
   * 一度でも画面に出てきた料理（NEXT に並んだ・作った）。
   * まだ出会っていない料理は図鑑で「？」にする。作った料理（unlockedItemIds）は必ず出会っている。
   */
  seenItemIds: string[];
  /** 開いた背景ツアー豆知識のID一覧。 */
  discoveredTipIds: string[];
  /** 背景ツアー「福岡めぐり」の累積経過時間（ミリ秒）。ゲームをまたいで保存される。 */
  tourElapsedMs: number;
  settings: GameSettings;
}

export const SAVE_VERSION = 3;

export function createDefaultSaveData(): SaveData {
  return {
    version: SAVE_VERSION,
    highScore: 0,
    highScoreAssisted: false,
    highScoreNoAssist: 0,
    totalScore: 0,
    playCount: 0,
    maxLevelReached: 0,
    bestLevelNoAssist: 0,
    unlockedItemIds: [],
    seenItemIds: [],
    discoveredTipIds: [],
    tourElapsedMs: 0,
    settings: { ...DEFAULT_SETTINGS },
  };
}

/** localStorage を安全に取得する（使えない場合は null）。 */
export function detectStorage(): StorageLike | null {
  try {
    const candidate = (globalThis as { localStorage?: StorageLike }).localStorage;
    if (!candidate) return null;
    const probeKey = '__fmm_probe__';
    candidate.setItem(probeKey, '1');
    candidate.removeItem(probeKey);
    return candidate;
  } catch {
    return null;
  }
}

function clamp01(value: unknown, fallback: number): number {
  const num = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(num)) return fallback;
  return Math.min(1, Math.max(0, num));
}

function toBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function toCount(value: unknown, fallback: number): number {
  const num = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(num) || num < 0) return fallback;
  return Math.floor(num);
}

/** 任意の入力を安全な SaveData へ正規化する（壊れたデータの復旧）。 */
export function normalizeSaveData(input: unknown): SaveData {
  const defaults = createDefaultSaveData();
  if (!input || typeof input !== 'object' || Array.isArray(input)) return defaults;

  const raw = input as Record<string, unknown>;
  const rawSettings =
    raw.settings && typeof raw.settings === 'object' && !Array.isArray(raw.settings)
      ? (raw.settings as Record<string, unknown>)
      : {};

  const unlocked = Array.isArray(raw.unlockedItemIds)
    ? raw.unlockedItemIds.filter((id): id is string => typeof id === 'string' && id.length > 0)
    : [];

  const seen = Array.isArray(raw.seenItemIds)
    ? raw.seenItemIds.filter((id): id is string => typeof id === 'string' && id.length > 0)
    : [];

  const discoveredTips = Array.isArray(raw.discoveredTipIds)
    ? raw.discoveredTipIds.filter((id): id is string => typeof id === 'string' && id.length > 0)
    : [];

  const maxLevelReached = toCount(raw.maxLevelReached, defaults.maxLevelReached);
  const highScore = toCount(raw.highScore, defaults.highScore);

  return {
    version: SAVE_VERSION,
    highScore,
    highScoreAssisted: highScore > 0 && raw.highScoreAssisted === true,
    // 古いデータ（項目なし）はどちらだったか分からないので、ハイスコアをそのまま引き継ぐ
    highScoreNoAssist: Math.min(
      highScore,
      raw.highScoreNoAssist === undefined ? highScore : toCount(raw.highScoreNoAssist, 0),
    ),
    totalScore: toCount(raw.totalScore, defaults.totalScore),
    playCount: toCount(raw.playCount, defaults.playCount),
    maxLevelReached,
    // 古いデータ（項目なし）はアシストが無かった時代の記録なので、到達レベルをそのまま引き継ぐ
    bestLevelNoAssist: Math.min(
      maxLevelReached,
      raw.bestLevelNoAssist === undefined
        ? maxLevelReached
        : toCount(raw.bestLevelNoAssist, 0),
    ),
    unlockedItemIds: [...new Set(unlocked)],
    seenItemIds: [...new Set([...unlocked, ...seen])],
    discoveredTipIds: [...new Set(discoveredTips)],
    tourElapsedMs: toCount(raw.tourElapsedMs, defaults.tourElapsedMs),
    settings: {
      bgmVolume: clamp01(rawSettings.bgmVolume, defaults.settings.bgmVolume),
      seVolume: clamp01(rawSettings.seVolume, defaults.settings.seVolume),
      showGuide: toBoolean(rawSettings.showGuide, defaults.settings.showGuide),
      showShake: toBoolean(rawSettings.showShake, defaults.settings.showShake),
      showEvolutionList: toBoolean(
        rawSettings.showEvolutionList,
        defaults.settings.showEvolutionList,
      ),
      largeText: toBoolean(rawSettings.largeText, defaults.settings.largeText),
      recordHighlights: toBoolean(rawSettings.recordHighlights, defaults.settings.recordHighlights),
      reduceMotion: toBoolean(rawSettings.reduceMotion, defaults.settings.reduceMotion),
      sendUsageStats: toBoolean(rawSettings.sendUsageStats, defaults.settings.sendUsageStats),
      streamerMode: toBoolean(rawSettings.streamerMode, defaults.settings.streamerMode),
      bowlSkin:
        typeof rawSettings.bowlSkin === 'string' && rawSettings.bowlSkin.length > 0
          ? rawSettings.bowlSkin
          : defaults.settings.bowlSkin,
    },
  };
}

/** StorageSystem の構成。 */
export interface StorageSystemOptions {
  /** ステージ別の記録の保存キー。 */
  key: string;
  /**
   * 設定の保存キー（任意）。
   * 指定するとステージをまたいで設定を共有する。省略時は記録と同じキーに入る。
   */
  settingsKey?: string;
  /**
   * 旧バージョンの保存キー（任意）。
   * `key` にデータが無く、ここにデータがある場合だけ1度移行する。
   */
  legacyKey?: string;
  /**
   * 料理・豆知識のIDを変えたときの引き継ぎ表（任意）。
   * 読み込み時に旧IDを新IDへ置き換え、変わった場合だけ保存し直す。
   * 置き換える前のデータは `<key>:before-id-migration` に1度だけ控えを残す。
   */
  idMigration?: StageIdMigration;
  storage?: StorageLike | null;
}

/** ID引き継ぎの前のデータを控えておくキー。 */
export function idMigrationBackupKey(key: string): string {
  return `${key}:before-id-migration`;
}

/**
 * 保存データの料理ID・豆知識IDを引き継ぎ表で置き換える（純粋な関数）。
 * 変化が無ければ `changed: false`。
 */
export function applyIdMigration(
  data: SaveData,
  migration: StageIdMigration | undefined,
): { data: SaveData; changed: boolean } {
  if (!migration) return { data, changed: false };
  const unlockedItemIds = migrateIdList(data.unlockedItemIds, migration.items);
  // 作った料理は必ず出会っている（normalizeSaveData と同じ並び：作った料理が先）
  const seenItemIds = migrateIdList([...unlockedItemIds, ...data.seenItemIds], migration.items);
  const discoveredTipIds = migrateIdList(data.discoveredTipIds, migration.tips);
  const same = (a: readonly string[], b: readonly string[]) =>
    a.length === b.length && a.every((v, i) => v === b[i]);
  const changed =
    !same(unlockedItemIds, data.unlockedItemIds) ||
    !same(seenItemIds, data.seenItemIds) ||
    !same(discoveredTipIds, data.discoveredTipIds);
  if (!changed) return { data, changed: false };
  return { data: { ...data, unlockedItemIds, seenItemIds, discoveredTipIds }, changed: true };
}

export class StorageSystem {
  private readonly key: string;
  private readonly settingsKey?: string;
  private readonly legacyKey?: string;
  private readonly idMigration?: StageIdMigration;
  private readonly storage: StorageLike | null;
  private data: SaveData;
  private failed = false;

  constructor(
    keyOrOptions: string | StorageSystemOptions,
    storage: StorageLike | null = detectStorage(),
  ) {
    const options: StorageSystemOptions =
      typeof keyOrOptions === 'string' ? { key: keyOrOptions, storage } : keyOrOptions;
    this.key = options.key;
    this.settingsKey = options.settingsKey;
    this.legacyKey = options.legacyKey;
    this.idMigration = options.idMigration;
    this.storage = options.storage !== undefined ? options.storage : detectStorage();
    this.data = this.read();
  }

  /** ストレージが利用可能かどうか。 */
  get available(): boolean {
    return this.storage !== null && !this.failed;
  }

  get snapshot(): Readonly<SaveData> {
    return this.data;
  }

  get settings(): GameSettings {
    return { ...this.data.settings };
  }

  get highScore(): number {
    return this.data.highScore;
  }

  /** 全プレイの合計スコア。 */
  get totalScore(): number {
    return this.data.totalScore;
  }

  get unlockedItemIds(): string[] {
    return [...this.data.unlockedItemIds];
  }

  get seenItemIds(): string[] {
    return [...this.data.seenItemIds];
  }

  /** 図鑑で名前と絵を見せてよいか（作った、または一度でも出てきた）。 */
  isRevealed(itemId: string): boolean {
    return this.data.seenItemIds.includes(itemId) || this.data.unlockedItemIds.includes(itemId);
  }

  /** 出会ったことにする。新しく出会った場合のみ true。 */
  markSeen(itemId: string): boolean {
    if (this.data.seenItemIds.includes(itemId)) return false;
    this.data.seenItemIds.push(itemId);
    this.persist();
    return true;
  }

  get discoveredTipIds(): string[] {
    return [...this.data.discoveredTipIds];
  }

  get tourElapsedMs(): number {
    return this.data.tourElapsedMs;
  }

  get playCount(): number {
    return this.data.playCount;
  }

  get maxLevelReached(): number {
    return this.data.maxLevelReached;
  }

  /** いまのハイスコアが、コンティニュー・おはしを使ったプレイの記録か。 */
  get highScoreAssisted(): boolean {
    return this.data.highScoreAssisted;
  }

  /** コンティニューもおはしも使わなかったプレイだけのハイスコア。 */
  get highScoreNoAssist(): number {
    return this.data.highScoreNoAssist;
  }

  /**
   * ハイスコアを更新する。更新された場合のみ true。
   * `assisted`（コンティニュー・おはしを使ったプレイ）でもハイスコアは更新するが、印を付けて覚えておく。
   * 使っていないプレイは highScoreNoAssist にも記録する。
   */
  submitScore(score: number, options: { assisted?: boolean } = {}): boolean {
    const value = Math.floor(score);
    const assisted = options.assisted === true;
    let changed = false;
    if (!assisted && value > this.data.highScoreNoAssist) {
      this.data.highScoreNoAssist = value;
      changed = true;
    }
    const isNewRecord = value > this.data.highScore;
    if (isNewRecord) {
      this.data.highScore = value;
      this.data.highScoreAssisted = assisted;
      changed = true;
    }
    if (changed) this.persist();
    return isNewRecord;
  }

  /**
   * 1プレイ分のスコアを累計へ加算する。
   * ハイスコアと違い、下手なプレイでも必ず積み上がるので、
   * 「遊べば遊ぶほどごほうびが解放される」導線に使える。
   */
  addTotalScore(score: number): number {
    const gain = Math.max(0, Math.floor(score));
    if (gain === 0) return this.data.totalScore;
    this.data.totalScore += gain;
    this.persist();
    return this.data.totalScore;
  }

  updateSettings(partial: Partial<GameSettings>): GameSettings {
    this.data.settings = { ...this.data.settings, ...partial };
    this.persist();
    return this.settings;
  }

  /** 図鑑を解放する。新規解放の場合のみ true。 */
  unlockItem(itemId: string): boolean {
    if (this.data.unlockedItemIds.includes(itemId)) return false;
    this.data.unlockedItemIds.push(itemId);
    if (!this.data.seenItemIds.includes(itemId)) this.data.seenItemIds.push(itemId);
    this.persist();
    return true;
  }

  /** 豆知識を発見済みにする。新規発見の場合のみ true。 */
  discoverTip(tipId: string): boolean {
    if (this.data.discoveredTipIds.includes(tipId)) return false;
    this.data.discoveredTipIds.push(tipId);
    this.persist();
    return true;
  }

  /**
   * 背景ツアーの累積経過時間を加算する。
   * ゲームをまたいで「遊ぶほど旅が進む」時間を保存する。
   */
  addTourElapsedMs(ms: number): number {
    const gain = Math.max(0, Math.floor(ms));
    if (gain === 0) return this.data.tourElapsedMs;
    this.data.tourElapsedMs += gain;
    this.persist();
    return this.data.tourElapsedMs;
  }

  /** 背景ツアーの累積経過時間を 0 に戻す（設定からのリセット用）。発見済みチップスは消さない。 */
  resetTourElapsedMs(): void {
    this.data.tourElapsedMs = 0;
    this.persist();
  }

  incrementPlayCount(): number {
    this.data.playCount += 1;
    this.persist();
    return this.data.playCount;
  }

  get bestLevelNoAssist(): number {
    return this.data.bestLevelNoAssist;
  }

  /**
   * 1プレイの到達レベルを記録する。最高到達が更新された場合のみ true。
   *
   * `assisted`（コンティニュー・アイテムを使ったプレイ）でも最高到達は更新する。
   * 使わずに届いたときだけ `bestLevelNoAssist` も更新する。
   */
  submitMaxLevel(level: number, options: { assisted?: boolean } = {}): boolean {
    const value = Math.floor(level);
    let changed = false;
    let raised = false;
    if (value > this.data.maxLevelReached) {
      this.data.maxLevelReached = value;
      changed = true;
      raised = true;
    }
    if (!options.assisted && value > this.data.bestLevelNoAssist) {
      this.data.bestLevelNoAssist = value;
      changed = true;
    }
    if (changed) this.persist();
    return raised;
  }

  /** 保存データを初期化する。 */
  resetAll(): void {
    this.data = createDefaultSaveData();
    this.persist();
  }

  private read(): SaveData {
    if (!this.storage) return createDefaultSaveData();

    let raw = this.safeGet(this.key);
    if (!raw) raw = this.migrateFromLegacy();

    let data: SaveData;
    try {
      data = raw ? normalizeSaveData(JSON.parse(raw)) : createDefaultSaveData();
    } catch (error) {
      console.warn('[StorageSystem] 保存データを読み込めませんでした。初期値で起動します。', error);
      data = createDefaultSaveData();
    }

    // 公開後に変えた料理ID・豆知識IDを引き継ぐ
    if (raw && this.idMigration) {
      const migrated = applyIdMigration(data, this.idMigration);
      if (migrated.changed) {
        data = migrated.data;
        this.saveMigrated(raw, data);
      }
    }

    // 設定を別キーで持っている場合は、そちらを優先する（ステージ間で共有するため）
    if (this.settingsKey) {
      const rawSettings = this.safeGet(this.settingsKey);
      if (rawSettings) {
        try {
          data.settings = normalizeSaveData({ settings: JSON.parse(rawSettings) }).settings;
        } catch {
          // 壊れていれば記録側の設定をそのまま使う
        }
      }
    }
    return data;
  }

  /**
   * 旧バージョンのキーから1度だけ移行する。
   *
   * 福岡単独だった頃のデータを、ステージ別のキーへ移す。
   * **旧キーは消さない。** 移行に不具合があったときに手で戻せるようにしておく。
   * 戻り値は移行したデータの生文字列（移行しなかった場合は null）。
   */
  private migrateFromLegacy(): string | null {
    if (!this.legacyKey || !this.storage) return null;
    const legacy = this.safeGet(this.legacyKey);
    if (!legacy) return null;

    try {
      this.storage.setItem(this.key, legacy);
      // 設定も共有キーへ移す（まだ無い場合のみ）
      if (this.settingsKey && !this.safeGet(this.settingsKey)) {
        const parsed = normalizeSaveData(JSON.parse(legacy));
        this.storage.setItem(this.settingsKey, JSON.stringify(parsed.settings));
      }
    } catch (error) {
      console.warn('[StorageSystem] 旧データの移行に失敗しました。', error);
      return legacy;
    }
    return legacy;
  }

  /**
   * ID引き継ぎの結果を保存する。
   * **置き換える前のデータは控えキーに1度だけ残す。** 表を書き間違えたときに手で戻せるように。
   * 設定キーには触らない（設定はIDと関係がない）。
   */
  private saveMigrated(raw: string, data: SaveData): void {
    if (!this.storage) return;
    try {
      const backupKey = idMigrationBackupKey(this.key);
      if (!this.safeGet(backupKey)) this.storage.setItem(backupKey, raw);
      this.storage.setItem(this.key, JSON.stringify(data));
    } catch (error) {
      // 保存できなくても、メモリ上では引き継いだ状態で遊べる
      console.warn('[StorageSystem] ID引き継ぎの保存に失敗しました。', error);
    }
  }

  private safeGet(key: string): string | null {
    if (!this.storage) return null;
    try {
      return this.storage.getItem(key);
    } catch {
      return null;
    }
  }

  private persist(): void {
    if (!this.storage || this.failed) return;
    try {
      this.storage.setItem(this.key, JSON.stringify(this.data));
      if (this.settingsKey) {
        this.storage.setItem(this.settingsKey, JSON.stringify(this.data.settings));
      }
    } catch (error) {
      this.failed = true;
      console.warn('[StorageSystem] 保存に失敗しました。以降はメモリ上のみで動作します。', error);
    }
  }
}
